import { useState, memo } from 'react';
import { useHistory } from 'react-router-dom';
import { CaseBaseModel } from 'models';
import { RequestTypeCode } from 'utils/enums';
import format from 'date-fns/format';
import { fi } from 'date-fns/locale';
import { toDate } from 'utils';
import {
  getFirstWidgetSymbolsOrder,
  getSecondWidgetSymbolsOrder,
  isIncident,
  isKeyOrder,
  isRepair,
} from './helpers';
import services from 'services';

import { KeyOrderViewForm } from 'pages/key-orders';
import { UserSpaceRenovationRequestViewForm } from 'pages/repair';
import { UserCommonCaseViewForm } from 'pages/incident';

import { WidgetContainer, CaseIcon, Confirmation, Modal } from 'components';
import CaseWidgetTimeline from './case-widget-timeline';
import { Box } from '@material-ui/core';
import { IconButton } from 'components';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

import PageLayout from 'layouts/PageLayout';

import clsx from 'clsx';
import { useStyles } from './style';

interface Props {
  baseCases: CaseBaseModel[];
  handleChangeBaseCase: (newBaseCase: CaseBaseModel) => void;
}

type CaseEntity = 'keyorder' | 'incident' | 'repair';

interface CaseState {
  open: boolean;
  id: string | null;
  entity: CaseEntity | null;
}

const CaseWidget: React.FC<Props> = ({ baseCases, handleChangeBaseCase }) => {
  const classes = useStyles();
  const history = useHistory();

  const [selectedCase, setSelectedCase] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [rejectCaseState, setRejectCaseState] = useState({
    loading: false,
    error: false,
  });
  const [caseState, setCaseState] = useState<CaseState>({
    open: false,
    id: null,
    entity: null,
  });

  const baseCase = baseCases[selectedCase] || undefined;

  const handleNext = () => {
    setSelectedCase((prevState) => prevState + 1);
  };

  const handlePrev = () => {
    setSelectedCase((prevState) => prevState - 1);
  };

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleCloseCase = () => {
    setCaseState((prevState) => ({
      ...prevState,
      open: false,
    }));
  };

  const handleInitCaseId = () => {
    setCaseState((prevState) => ({
      ...prevState,
      id: null,
      entity: null,
    }));
  };

  const rejectCase = async () => {
    try {
      setRejectCaseState((prevState) => ({ ...prevState, loading: true }));

      const newBaseCase = (
        await services.put(`/api/user/cases/${baseCase?.Id}/cancel`)
      ).data.Data;

      handleChangeBaseCase(newBaseCase);
      setRejectCaseState((prevState) => ({
        ...prevState,
        loading: false,
      }));
      handleCloseModal();
    } catch (err) {
      console.error(err);
      setRejectCaseState((prevState) => ({
        ...prevState,
        error: true,
        loading: false,
      }));
    }
  };

  const renderEmptyCase = () => (
    <>
      <CaseIcon />
      <Box ml={2}>
        <span className={clsx(classes.text, classes.bold)}>
          Tässä näet palvelupyyntöjesi tilanteen.
        </span>
        <span className={clsx(classes.text, classes.greyish)}>
          Ei avoimia palvelupyyntöjä.
        </span>
      </Box>
    </>
  );

  const renderCaseItem = (caseBase: CaseBaseModel) => {
    const { RequestType, CreatedOn, Status } = caseBase;
    let d = toDate(CreatedOn);

    let { green, blue, red, order } =
      RequestType?.Value !== RequestTypeCode.PTS &&
      RequestType?.Value !== RequestTypeCode.SpaceRenovationRequest
        ? getFirstWidgetSymbolsOrder(Status?.Value)
        : RequestType.Value === RequestTypeCode.SpaceRenovationRequest
        ? getSecondWidgetSymbolsOrder(Status?.Value)
        : { order: [], red: [], green: [], blue: [] };

    let entity: CaseEntity | null = null;

    if (RequestType?.Value) {
      const { Value } = RequestType;
      entity = isIncident(Value)
        ? 'incident'
        : isRepair(Value)
        ? 'repair'
        : isKeyOrder(Value)
        ? 'keyorder'
        : null;
    }

    const handleClickCase = () => {
      setCaseState({
        entity,
        id: caseBase.Id,
        open: true,
      });
    };

    return (
      <div
        className={classes.caseItem}
        onClick={entity ? handleClickCase : undefined}
      >
        <span className={classes.caseItemTitle}>
          {caseBase.RequestType?.Value === RequestTypeCode.Case
            ? caseBase.CaseLevel1?.Label
            : caseBase.RequestType?.Label}
        </span>
        <span className={classes.caseItemDate}>
          <span className={classes.weekDay}>
            {d ? format(d, 'EEEE', { locale: fi }) : null}
          </span>{' '}
          {d ? format(d, 'd.M.yyyy', { locale: fi }) : null}
        </span>
        <CaseWidgetTimeline order={order} green={green} red={red} blue={blue} />
      </div>
    );
  };

  const renderSlider = () => {
    if (!baseCase || !baseCases) return null;

    return (
      <Box display="flex" alignItems="center" width="100%">
        <IconButton
          className={classes.iconBtn}
          onClick={handlePrev}
          disabled={selectedCase === 0}
        >
          <ChevronLeftIcon className={classes.arrowIcon} />
        </IconButton>
        {renderCaseItem(baseCase)}
        <IconButton
          className={classes.iconBtn}
          onClick={handleNext}
          disabled={baseCases && selectedCase === baseCases.length - 1}
        >
          <ChevronRightIcon className={classes.arrowIcon} />
        </IconButton>
      </Box>
    );
  };

  const renderNumeration = () => {
    return baseCases && baseCases.length
      ? `${selectedCase + 1}/${baseCases.length}`
      : null;
  };

  const handleWidgetAction = () => {
    let url = '/';

    if (!baseCases.length) {
      url = '/incident';
    } else {
      if (baseCase && baseCase.RequestType?.Value) {
        const { Value } = baseCase.RequestType;

        url = isIncident(Value)
          ? '/incident'
          : isRepair(Value)
          ? '/repair'
          : isKeyOrder(Value)
          ? '/key-order'
          : '/';
      }
    }

    history.push(url);
  };

  const showCases = baseCases && baseCases.length;

  return (
    <>
      <WidgetContainer
        title={
          !baseCase ||
          (baseCase.RequestType?.Value !== RequestTypeCode.PTS &&
            baseCase.RequestType?.Value !== RequestTypeCode.KeyOrder &&
            baseCase.RequestType?.Value !==
              RequestTypeCode.SpaceRenovationRequest)
            ? 'Kaikki palvelupyynnöt'
            : baseCase.RequestType?.Value ===
              RequestTypeCode.SpaceRenovationRequest
            ? 'Kaikki huoneiston muutostyöt'
            : baseCase.RequestType?.Value === RequestTypeCode.KeyOrder
            ? 'Kaikki avaintilaukset'
            : ''
        }
        action={handleWidgetAction}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          marginBottom="15px"
          padding={{ xs: '5px', sm: '15px' }}
        >
          <h3 className={classes.title}>Palvelupyynnöt {renderNumeration()}</h3>
          {/* {showCases ? (
            baseCase?.Status?.Value !== CaseStatuses.Completed &&
            baseCase?.Status?.Value !== CaseStatuses.Canceled ? (
              <Button className={classes.btn} onClick={handleOpenModal}>
                Peru
              </Button>
            ) : null
          ) : null} */}
        </Box>
        <Box
          position="relative"
          display="flex"
          alignItems="center"
          height="100%"
          padding={{
            xs: `${showCases ? '0px 2px' : '30px 10px'} 30px`,
            sm: `${showCases ? 0 : 30}px 10px 30px`,
          }}
        >
          {showCases ? renderSlider() : renderEmptyCase()}
        </Box>
      </WidgetContainer>
      <Modal
        open={openModal}
        maxWidth="xs"
        fullWidth
        handleClose={handleCloseModal}
        cls={{
          childrenRoot: classes.modal,
        }}
      >
        <Confirmation
          borderClassName={classes.confirmationBorder}
          title={{
            text: 'Haluatko peruuttaa palvelupyynnön?',
          }}
          applyButton={{
            className: classes.confirmationBtn,
            text: 'Peruuta palvelupyyntö',
            click: rejectCase,
            loader: rejectCaseState.loading,
            disabled: rejectCaseState.loading,
          }}
          rejectButton={{
            className: clsx(classes.confirmationBtn, classes.rejectBtn),
            text: 'En, Palaa takaisin',
            click: handleCloseModal,
            disabled: rejectCaseState.loading,
          }}
        />
      </Modal>
      <Modal
        open={caseState.open}
        fullWidth
        maxWidth="md"
        handleClose={handleCloseCase}
        onExited={handleInitCaseId}
      >
        <PageLayout>
          {caseState.id ? (
            caseState.entity === 'incident' ? (
              <UserCommonCaseViewForm caseId={caseState.id} />
            ) : caseState.entity === 'keyorder' ? (
              <KeyOrderViewForm caseId={caseState.id} />
            ) : caseState.entity === 'repair' ? (
              <UserSpaceRenovationRequestViewForm caseId={caseState.id} />
            ) : null
          ) : null}
        </PageLayout>
      </Modal>
    </>
  );
};

export default memo(CaseWidget);
